import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { useQuery, graphql } from 'relay-hooks';

import { RELAY_CONNECTION_MAX } from 'ms-utils/relay';
import extractNode from 'ms-utils/relay/extractNode';

import { TeacherContext } from '.';
import type {
  SchoolContextQuery,
  SchoolContextQueryResponse,
} from './__generated__/SchoolContextQuery.graphql';

type ClassConnection = NonNullable<
  NonNullable<SchoolContextQueryResponse['school']>['schoolClasses']
>;
type Nodes<
  T extends Readonly<{
    edges: ReadonlyArray<{
      node: any;
    }>;
  }>,
> = ReadonlyArray<T['edges'][number]['node']>;
export type ClassList = Nodes<ClassConnection>;
type SchoolContextType = {
  schoolClasses: ClassList | null;
};
export const SchoolContext = createContext<SchoolContextType>({
  schoolClasses: [],
});
export const CLASS_FRAGMENT = graphql`
  fragment SchoolContext_class on Class {
    id
    pk
    code
    title
    displayName
    # This is used throughout the NTE to check whether a class has access to certain features
    # such as editing class details
    isPartnerClass
    isUnsyncedClass
    # This is used in class reports as adding a query there specifically to get a class' student
    # count creates a flickering effect on the count when a new report is opened
    studentCount
    students(first: $numberOfStudents) {
      edges {
        node {
          id
          user {
            id
            firstName
            lastName
            avatar
          }
        }
      }
    }
    hasTasks
    tasksCount
    classSyllabusFocus {
      id
    }
    classGrade {
      id
    }
    school {
      id
      title
    }
  }
`;
export function SchoolContextProvider({ children }: { children: ReactNode }) {
  const { schoolId } = useContext(TeacherContext);
  const { props, error } = useQuery<SchoolContextQuery>(
    graphql`
      query SchoolContextQuery(
        $schoolId: ID!
        $numberOfClasses: Int!
        $numberOfStudents: Int!
      ) {
        school(id: $schoolId) {
          schoolClasses: classes(first: $numberOfClasses)
            @connection(key: "School_schoolClasses") {
            edges {
              node {
                ...SchoolContext_class @relay(mask: false)
              }
            }
          }
        }
      }
    `,
    {
      schoolId,
      numberOfClasses: RELAY_CONNECTION_MAX,
      numberOfStudents: RELAY_CONNECTION_MAX,
    },
  );
  const classNodes = props?.school?.schoolClasses;
  const schoolClasses =
    error !== null || classNodes == null ? null : extractNode(classNodes);
  return (
    <SchoolContext.Provider value={{ schoolClasses }}>
      {children}
    </SchoolContext.Provider>
  );
}
export default function useSchoolContext() {
  return useContext(SchoolContext);
}
